import type { PageDescriptor } from "@pm2/api";
import {
  AccessCodes,
  getPageContent,
  getPageMetadata,
  getTeacherGuide,
} from "@pm2/api";
import type { AccordionComponentNode } from "@pm2/content-engine";
import { makeAccordionComponentParsers } from "@pm2/content-engine";
import { verticalPaddingCls } from "@pm2/react-utils";
import type { PM2SitemapStore } from "@pm2/sitemap-utils";
import { findCourseDescriptorFromParents } from "@pm2/sitemap-utils";
import { type PM2ConfigStore } from "@pm2/store";
import { MissingAccessMessage, NotFound } from "@pm2/ui-components";
import { BottomNavigation } from "@pm2/ui-layout";
import type { UserIdentity } from "@provider/user-identity";
import { xml2ast } from "@utils/ast";

export async function renderTeacherGuidePage(
  user: UserIdentity | null | undefined,
  configStore: PM2ConfigStore,
  sitemapStore: PM2SitemapStore,
  pageDescriptor: PageDescriptor,
): Promise<JSX.Element | null> {
  const configState = configStore.getState();
  const chapterDescriptor = findCourseDescriptorFromParents(
    sitemapStore.getState(),
    pageDescriptor,
  );

  if (!chapterDescriptor) {
    return (
      <>
        <NotFound />

        <BottomNavigation pageDescriptor={pageDescriptor} />
      </>
    );
  }

  if (pageDescriptor.accessCode !== AccessCodes.ALLOWED) {
    return (
      <>
        <div className={verticalPaddingCls}>
          <MissingAccessMessage requiredLicenseType="page" />
        </div>

        <BottomNavigation pageDescriptor={pageDescriptor} />
      </>
    );
  }

  const [
    { TeacherGuidePage, extractCourseMetadata },
    metadata,
    teacherGuide,
    teacherGuidePageContent,
  ] = await Promise.all([
    import("@pm2/course-frontpage"),
    getPageMetadata(configState.config.id, chapterDescriptor.id, {
      previewMode: configState.previewMode,
    }),
    getTeacherGuide(configState.config.id, chapterDescriptor.id, {
      previewMode: configState.previewMode,
    }),
    getPageContent(configState.config.id, pageDescriptor.id, {
      previewMode: configState.previewMode,
    }),
  ]);

  const teachersGuidePageAccordions = xml2ast(
    teacherGuidePageContent,
    makeAccordionComponentParsers({ disableAutoCollapse: true }),
    "text/xml",
    { parseText: false, warnOnUnknownNode: false },
  ) as AccordionComponentNode[];

  return (
    <>
      <TeacherGuidePage
        courseDescriptor={pageDescriptor}
        metadata={extractCourseMetadata(metadata)}
        teacherGuide={teacherGuide}
        teachersGuidePageAccordions={teachersGuidePageAccordions}
        isAuthorized={pageDescriptor.accessCode === AccessCodes.ALLOWED}
      />

      <BottomNavigation pageDescriptor={pageDescriptor} />
    </>
  );
}
